// «Mensaje a un área» (shared/API.md §37): which channels the picker lists and in what order.
// Twin of the native `AreaChannelPicker` written from the same contract.
//
// The team's PUBLIC channels named `area-<satori_area_id>` (Core keeps them; anyone may join and
// post), archived ones out; your own área first, then by name (Spanish collation). Each row reads
// «Área · Legal · 3 personas» — the display name, then `member_count` when the server told us.
import { compareAreaNames, isAreaChannelName, AREA_CHANNEL_PREFIX } from './areaGrouping';
import { t, tf } from '../i18n/i18n';

export interface AreaChannelCandidate {
  id: string;
  name: string;
  displayName: string;
  type: string;
  deleteAt: number;
  joined: boolean;
  /** null = unknown (the count endpoint is best-effort). */
  memberCount: number | null;
}

export interface AreaChannelOption {
  id: string;
  name: string;
  displayName: string;
  joined: boolean;
  memberCount: number | null;
  /** The reader's own área (`users.satori_area_id` matches the channel's id suffix). */
  isMine: boolean;
}

/** The Core área id a channel name carries (`area-10` → "10"), or null for any other channel. */
export function areaIdOf(channelName: string): string | null {
  return isAreaChannelName(channelName) ? channelName.slice(AREA_CHANNEL_PREFIX.length) : null;
}

export function options(candidates: readonly AreaChannelCandidate[], myAreaId: string | null | undefined): AreaChannelOption[] {
  const mine = myAreaId?.trim() || null;
  const seen = new Set<string>();
  const out: AreaChannelOption[] = [];
  for (const c of candidates) {
    if (!c.id || seen.has(c.id) || c.type !== 'O' || c.deleteAt !== 0 || !isAreaChannelName(c.name)) continue;
    seen.add(c.id);
    out.push({
      id: c.id,
      name: c.name,
      displayName: c.displayName || c.name,
      joined: c.joined,
      memberCount: c.memberCount,
      isMine: mine !== null && areaIdOf(c.name) === mine,
    });
  }
  return out.sort((a, b) => (a.isMine !== b.isMine ? (a.isMine ? -1 : 1) : compareAreaNames(a.displayName, b.displayName)));
}

/** «3 personas» / «1 persona», or null when the count is unknown. */
export function peopleCount(memberCount: number | null): string | null {
  if (memberCount == null || memberCount < 0) return null;
  return memberCount === 1 ? t('web.area.peopleOne') : tf('web.area.people', memberCount);
}

/** The row's one line: «Área · Legal · 3 personas». */
export function rowLine(option: AreaChannelOption): string {
  const count = peopleCount(option.memberCount);
  return count ? `${option.displayName} · ${count}` : option.displayName;
}

/** Case/accent-insensitive filter over the display name (the picker's search field). */
export function filter(list: readonly AreaChannelOption[], query: string): AreaChannelOption[] {
  const fold = (s: string) => s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
  const needle = fold(query.trim());
  if (!needle) return [...list];
  return list.filter((o) => fold(o.displayName).includes(needle));
}

export const AreaChannelPicker = { areaIdOf, options, peopleCount, rowLine, filter } as const;
